/**
 * Donut Chart
 * Ring segments for sport/activity breakdown by duration.
 * Center shows total hours; legend renders in a single flex row below the ring.
 */
import React, { useEffect, useRef } from 'react';
import { svgEl } from './svgUtils';
import type { DonutSeg } from './types';

const SURFACE_DARK = 'var(--chart-donut-center, #1A1E2B)';
const RING_VIEW_H = 204;

function renderRing(svg: SVGSVGElement, data: DonutSeg[]): void {
  svg.innerHTML = '';
  if (!data.length) return;

  const cx = 136, cy = 96, R = 82, ri = 52;
  const total = data.reduce((s, d) => s + d.pct, 0);
  let angle = -Math.PI / 2;

  svg.appendChild(svgEl('circle', { cx, cy, r: ri + 4, fill: SURFACE_DARK }));

  data.forEach(d => {
    const sweep = (d.pct / total) * 2 * Math.PI;
    const gap = 0.035;
    const a0 = angle + gap / 2, a1 = angle + sweep - gap / 2;
    const lg = sweep > Math.PI ? 1 : 0;
    const cos = Math.cos, sin = Math.sin;
    const pd = [
      `M${(cx + R * cos(a0)).toFixed(2)},${(cy + R * sin(a0)).toFixed(2)}`,
      `A${R},${R} 0 ${lg},1 ${(cx + R * cos(a1)).toFixed(2)},${(cy + R * sin(a1)).toFixed(2)}`,
      `L${(cx + ri * cos(a1)).toFixed(2)},${(cy + ri * sin(a1)).toFixed(2)}`,
      `A${ri},${ri} 0 ${lg},0 ${(cx + ri * cos(a0)).toFixed(2)},${(cy + ri * sin(a0)).toFixed(2)}`,
      'Z',
    ].join(' ');
    svg.appendChild(svgEl('path', { d: pd, fill: d.color, stroke: 'var(--palette-surface)', 'stroke-width': 1.5 }));
    angle += sweep;
  });

  svg.appendChild(svgEl('circle', { cx, cy, r: ri, fill: 'none', stroke: 'rgba(255,255,255,.06)', 'stroke-width': 1 }));

  const isTimeFormat = data.some(d => d.hrs.includes(':'));
  let centerVal: string, centerLbl: string;
  if (isTimeFormat) {
    const totalMins = data.reduce((s, d) => {
      const [hStr, mStr] = d.hrs.split(':');
      return s + parseInt(hStr || '0') * 60 + parseInt(mStr || '0');
    }, 0);
    const hh = Math.floor(totalMins / 60), mm = totalMins % 60;
    centerVal = `${hh}:${String(mm).padStart(2, '0')}`;
    centerLbl = 'TOTAL HRS';
  } else {
    const tot = data.reduce((s, d) => s + (d.rawVal ?? 0), 0);
    centerVal = tot >= 1000 ? `${Math.round(tot / 1000)}k` : String(Math.round(tot));
    centerLbl = 'TOTAL';
  }

  const tv = svgEl('text', { x: cx, y: cy - 6, 'text-anchor': 'middle', 'font-size': 24, 'font-weight': 800, fill: 'var(--chart-donut-text, #EEF1F7)', 'font-family': 'Plus Jakarta Sans,Inter,sans-serif' });
  tv.textContent = centerVal;
  svg.appendChild(tv);

  const ts = svgEl('text', { x: cx, y: cy + 11, 'text-anchor': 'middle', 'font-size': 8, 'font-weight': 700, 'letter-spacing': '.14em', fill: 'var(--chart-donut-muted, #3A4257)', 'font-family': 'Plus Jakarta Sans,Inter,sans-serif' });
  ts.textContent = centerLbl;
  svg.appendChild(ts);
}

export interface ChartDonutProps {
  data: DonutSeg[];
  style?: React.CSSProperties;
}

export function ChartDonut({ data, style }: ChartDonutProps) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => { if (ref.current) renderRing(ref.current, data); }, [data]);

  return (
    <div className="chart-donut" style={{ width: '100%', ...style }}>
      <svg
        ref={ref}
        viewBox={`0 0 280 ${RING_VIEW_H}`}
        style={{ width: '100%', height: RING_VIEW_H, display: 'block' }}
      />
      {data.length > 0 && (
        <div className="chart-donut-legend">
          {data.map((d) => (
            <div key={d.label} className="chart-donut-legend__item">
              <span className="chart-donut-legend__dot" style={{ background: d.color }} />
              <span className="chart-donut-legend__label">{d.label}</span>
              <span className="chart-donut-legend__meta">{d.hrs}</span>
              <span className="chart-donut-legend__pct">{Math.round(d.pct)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
